import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Box, Combobox, Group, useCombobox } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import {
  FavoritesDropdown,
  PillsInput,
  SuggestionsDropdown,
} from './components';
import {
  findActiveFavorite,
  getQueryBuilderEntitiesWithKeyword,
  resolveOptionSubmitAction,
} from './helpers';
import { useQueryBuilder, useQueryBuilderShare } from './hooks';
import { QueryBuilderInputPropsType } from './types';

export const QueryBuilderInput: React.FC<QueryBuilderInputPropsType> = ({
  entities,
  savedQueries,
  onChange,
  onSaveQuery,
  onDeleteSavedQuery,
  shareQuery,
  initialAst,
}) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const combobox = useCombobox();

  const entitiesWithKeyword = useMemo(
    () =>
      getQueryBuilderEntitiesWithKeyword(entities, t('QUERY_BUILDER.KEYWORD')),
    [entities, t]
  );

  const {
    ast,
    context,
    handleTypingCommitAction,
    isSubmitDisabled,
    loadAst,
    pillsInputProps,
    suggestionsDropdownProps,
  } = useQueryBuilder({ entities: entitiesWithKeyword, onChange });

  const { handleShareQuery } = useQueryBuilderShare({ shareQuery });

  const activeFavorite = useMemo(
    () => findActiveFavorite(savedQueries, ast),
    [savedQueries, ast]
  );
  const isSaveDisabled = isSubmitDisabled || !ast;
  const isShareDisabled = isSubmitDisabled || !ast;

  const openDropdown = useCallback(() => {
    combobox.openDropdown();
    inputRef.current?.focus();
  }, [combobox, inputRef]);

  const handleOptionSubmit = (value: string) => {
    const result = resolveOptionSubmitAction(context, value);

    if (!result) return;

    handleTypingCommitAction(result.action);

    if (result.shouldReopenDropdownAfterSubmit) {
      openDropdown();
      return;
    }

    combobox.closeDropdown();
  };

  const handleFavoriteSelect = (favoriteId: string) => {
    const favorite = savedQueries.find(
      (savedQuery) => savedQuery.id === favoriteId
    );

    if (!favorite) return;

    loadAst(favorite.ast);
  };

  const handleSaveQuery = (name: string) => {
    if (!ast) return;

    onSaveQuery(name, ast);
  };

  const handleShare = () => {
    if (!ast) return;

    handleShareQuery(ast);
  };

  const loadAstRef = useRef(loadAst);
  loadAstRef.current = loadAst;

  useEffect(() => {
    if (!initialAst) return;

    loadAstRef.current(initialAst);
  }, [initialAst]);

  useEffect(() => {
    if (context !== 'expectValue') return;

    openDropdown();
  }, [context, openDropdown]);

  return (
    <Group gap="xs" wrap="nowrap" align="center">
      <FavoritesDropdown
        savedQueries={savedQueries}
        activeFavorite={activeFavorite}
        onSelect={handleFavoriteSelect}
        onDelete={onDeleteSavedQuery}
        saveDisabled={isSaveDisabled}
        isSaveActive={Boolean(activeFavorite)}
        onSave={handleSaveQuery}
      />
      <Box style={{ flex: 1, minWidth: 0 }}>
        <Combobox store={combobox} onOptionSubmit={handleOptionSubmit}>
          <Combobox.DropdownTarget>
            <PillsInput
              combobox={combobox}
              inputRef={inputRef}
              {...pillsInputProps}
              onShare={handleShare}
              isShareDisabled={isShareDisabled}
            />
          </Combobox.DropdownTarget>
          <SuggestionsDropdown {...suggestionsDropdownProps} />
        </Combobox>
      </Box>
    </Group>
  );
};
